/* eslint-disable no-unused-vars */
/* eslint-disable no-use-before-define */
/* eslint-disable no-extra-boolean-cast */
/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Input, Space, message, Spin, DatePicker, Dropdown } from 'antd';
import { SearchOutlined, DownloadOutlined, DownOutlined } from '@ant-design/icons';
import { ExportCsv, ExportPdf } from '@material-table/exporters';
import dayjs from 'dayjs';

import { Main } from '../../../styled';
import { DataService } from '../../../../config/dataService/dataService';
import { PageHeader } from '../../../../components/page-headers/page-headers';

const { RangePicker } = DatePicker;

function DataTable() {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [exportMenuVisible, setExportMenuVisible] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};

      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      let response = await DataService.post('/reports/company-stats', params);
      response = response.data;
      if (response.status) {
        const data = Array.isArray(response.data) ? response.data : [];
        setTableData(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to fetch data');
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const getFilteredData = () => {
    return tableData.filter(row =>
      row.company_name?.toLowerCase().includes(search.toLowerCase())
    );
  };

  const handleExportCsv = () => {
    try {
      const filteredData = getFilteredData();
      const headers = [
        {
          title: 'Company Name',
        },
        {
          title: 'Total Employees',
        },
        {
          title: 'Active Employees',
        },
        {
          title: 'Inactive Employees',
        },
        {
          title: 'Total Card Requests',
        },
        {
          title: 'Cards Pending',
        },
        {
          title: 'Cards Printed',
        },
        {
          title: 'Cards In Process',
        },
        {
          title: 'Cards Rejected',
        },
        {
          title: 'Total Training Requests',
        },
        {
          title: 'Training In Process',
        },
        {
          title: 'Training Pending',
        },
        {
          title: 'Training Completed',
        }
      ];


      const csvData = filteredData.map(row => [
        row.company_name,
        row.total_employees || 0,
        row.active_employees || 0,
        row.inactive_employees || 0,
        row.total_card_requests || 0,
        row.cards_pending || 0,
        row.cards_printed || 0,
        row.cards_in_process || 0,
        row.cards_rejected || 0,
        row.total_training_requests || 0,
        row.training_in_process || 0,
        row.training_pending || 0,
        row.training_completed || 0
      ]);

      const fileName = `company_statistics${dateRange ? `_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}` : ''}${search ? `_${search}` : ''}`;
      ExportCsv(headers, csvData, fileName);
      setExportMenuVisible(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      message.error('Failed to export CSV');
    }
  };

  const handleExportPdf = () => {
    try {
      const filteredData = getFilteredData();
      const headers = [
        {
          title: 'Company Name',
        },
        {
          title: 'Total Employees',
        },
        {
          title: 'Active Employees',
        },
        {
          title: 'Inactive Employees',
        },
        {
          title: 'Total Card Requests',
        },
        {
          title: 'Cards Pending',
        },
        {
          title: 'Cards Printed',
        },
        {
          title: 'Cards In Process',
        },
        {
          title: 'Cards Rejected',
        },
        {
          title: 'Total Training Requests',
        },
        {
          title: 'Training In Process',
        },
        {
          title: 'Training Pending',
        },
        {
          title: 'Training Completed',
        }
      ];

      const pdfData = filteredData.map(row => [
        row.company_name,
        row.total_employees || 0,
        row.active_employees || 0,
        row.inactive_employees || 0,
        row.total_card_requests || 0,
        row.cards_pending || 0,
        row.cards_printed || 0,
        row.cards_in_process || 0,
        row.cards_rejected || 0,
        row.total_training_requests || 0,
        row.training_in_process || 0,
        row.training_pending || 0,
        row.training_completed || 0
      ]);

      const fileName = `company_statistics${dateRange ? `_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}` : ''}${search ? `_${search}` : ''}`;
      ExportPdf(headers, pdfData, fileName);
      setExportMenuVisible(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error('Failed to export PDF');
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Company Name',
      dataIndex: 'company_name',
      key: 'company_name',
      fixed: 'left',
      width: 200,
    },
    {
      title: 'Total Employees',
      dataIndex: 'total_employees',
      key: 'total_employees',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Active Employees',
      dataIndex: 'active_employees',
      key: 'active_employees',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Inactive Employees',
      dataIndex: 'inactive_employees',
      key: 'inactive_employees',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Total Card Requests',
      dataIndex: 'total_card_requests',
      key: 'total_card_requests',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Cards Pending',
      dataIndex: 'cards_pending',
      key: 'cards_pending',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Cards Printed',
      dataIndex: 'cards_printed',
      key: 'cards_printed',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Cards In Process',
      dataIndex: 'cards_in_process',
      key: 'cards_in_process',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Cards Rejected',
      dataIndex: 'cards_rejected',
      key: 'cards_rejected',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Total Training Requests',
      dataIndex: 'total_training_requests',
      key: 'total_training_requests',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Training In Process',
      dataIndex: 'training_in_process',
      key: 'training_in_process',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Training Pending',
      dataIndex: 'training_pending',
      key: 'training_pending',
      align: 'right',
      render: (text) => text || 0,
    },
    {
      title: 'Training Completed',
      dataIndex: 'training_completed',
      key: 'training_completed',
      align: 'right',
      render: (text) => text || 0,
    },
  ], []);

  const filteredData = useMemo(() => getFilteredData(), [tableData, search]);

  const exportMenuItems = [
    {
      key: 'csv',
      label: 'Export as CSV',
      icon: <DownloadOutlined />,
      onClick: handleExportCsv,
    },
    {
      key: 'pdf',
      label: 'Export as PDF',
      icon: <DownloadOutlined />,
      onClick: handleExportPdf,
    },
  ];

  return (
    <>
      <PageHeader className="ninjadash-page-header-main" title="Registered Companies" routes={[]} />
      <Main>
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">Company Statistics</h1>
            <Space>
              <RangePicker
                onChange={handleDateRangeChange}
                value={dateRange}
                allowClear
                placeholder={['Start Date', 'End Date']}
              />
              <Input
                placeholder="Search companies..."
                prefix={<SearchOutlined />}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 200 }}
              />
              <Dropdown
                menu={{ items: exportMenuItems }}
                trigger={['click']}
                open={exportMenuVisible}
                onOpenChange={setExportMenuVisible}
              >
                <Button type="primary" icon={<DownOutlined />}>
                  Export
                </Button>
              </Dropdown>
            </Space>
          </div>

          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              scroll={{ x: 'max-content' }}
              pagination={false}
              size="middle"
              bordered
            />
          </Spin>
        </div>
      </Main>
    </>
  );
}

export default React.memo(DataTable);
